import React from 'react'
import type { FC } from 'react'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import Close from '@material-ui/icons/Close'

import { useGetOutreachEmailPreviewQuery } from '@homeviews/store'
import { Button } from '@homeviews/shared-ui'
import type { TemplateSlugs } from '@homeviews/outreach'
import { Typography } from '@homeviews/outreach'

const useStyles = makeStyles((theme) =>
  createStyles({
    overlay: {
      position: 'fixed',
      top: 0,
      right: 0,
      zIndex: 1400,
      width: '100%',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    contentWrapper: {
      position: 'fixed',
      left: '50%',
      top: '50%',
      zIndex: 1401,
      transform: 'translate(-50%, -50%)',
      width: '100%',
      maxWidth: '650px',
      maxHeight: '100%',
      overflowY: 'auto',
      borderRadius: 5,
      backgroundColor: theme.colors.white,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 30px',
      backgroundColor: theme.colors.bgColor,
      borderBottom: `2px solid ${theme.colors.grey250}`,
    },
    body: {
      position: 'relative',
      height: '100%',
    },
    iframe: {
      height: '600px',
      border: 'none',
    },
    clickPreventionIframeOverlay: {
      position: 'absolute',
      top: '2%',
      left: '25%',
      width: '50%',
      height: '94%',
      backgroundColor: 'transparent',
    },
    customH1: {
      '.MuiTypography-h1&': {
        marginBottom: '0',
      },
    },
    closeButton: {
      padding: '0',
      border: 'none',
      background: '0 0',
      appearance: 'none',
      cursor: 'pointer',
      color: theme.colors.grey600,
    },
    footer: {
      display: 'flex',
      justifyContent: 'center',
      padding: '20px',
      backgroundColor: theme.colors.bgColor,
      borderTop: `2px solid ${theme.colors.grey250}`,
    },
    primaryButton: {
      padding: '12px 84px',
    },
  }),
)

type ModalProps = {
  emailTemplateSlug: TemplateSlugs
  closeOnClick: () => void
  primaryOnClick: () => void
}

export const Modal: FC<ModalProps> = ({
  emailTemplateSlug,
  closeOnClick,
  primaryOnClick,
}) => {
  const classes = useStyles()

  const { data, isFetching } = useGetOutreachEmailPreviewQuery({
    template: emailTemplateSlug,
  })

  const emailSubject = () => {
    if (isFetching) return 'Loading email preview subject...'
    if (data) return data.subject
    else return 'An unknown error has occurred'
  }

  const iframeSrcDoc = () => {
    if (isFetching) return 'Loading email preview HTML...'
    if (data) return data.html
    else return 'An unknown error has occurred'
  }

  return (
    <>
      <div className={classes.overlay} />
      <div className={classes.contentWrapper}>
        <div className={classes.header}>
          <Typography variant="h1" className={classes.customH1}>
            {emailSubject()}
          </Typography>
          <button
            className={classes.closeButton}
            onClick={closeOnClick}
            data-testid="modal-top-right-close"
          >
            <Close />
          </button>
        </div>
        <div className={classes.body}>
          <div className={classes.clickPreventionIframeOverlay} />
          <iframe
            title={emailSubject()}
            className={classes.iframe}
            width="100%"
            srcDoc={iframeSrcDoc()}
          />
        </div>
        <div className={classes.footer}>
          <Button
            className={classes.primaryButton}
            customColor="primary"
            onClick={primaryOnClick}
          >
            Close
          </Button>
        </div>
      </div>
    </>
  )
}
